import { useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";
import PricingPlans from "./components/PricingPlans";
import PricingFAQ from "./components/PricingFAQ";

export default function PricingPage() {
  const [form, setForm] = useState({ name: "", email: "", plan: "Pro" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const data = new URLSearchParams();
    data.append("name", form.name);
    data.append("email", form.email);
    data.append("plan", form.plan);
    try {
      await fetch("https://readdy.ai/api/form/d75cv9ivu4krcbn3bkhg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-white border-b border-gray-100 text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">
            Simple Pricing
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Start Free. Grow Fast.
          </h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            14-day free trial on all plans. No credit card required. Cancel anytime.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-5 mt-8 text-xs text-gray-500">
            {[
              { icon: "ri-shield-check-line", text: "14-day free trial" },
              { icon: "ri-bank-card-line", text: "No card required" },
              { icon: "ri-close-circle-line", text: "Cancel anytime" },
              { icon: "ri-customer-service-line", text: "Live support" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-1.5">
                <i className={`${b.icon} text-emerald-500`} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <PricingPlans />
        </div>
      </section>

      {/* FAQ */}
      <PricingFAQ />

      {/* Trial Sign-Up Form */}
      <section id="trial-form" className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-lg mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Start Your Free 14-Day Trial</h2>
            <p className="text-gray-500 text-sm">No credit card. Full Pro access. Cancel anytime.</p>
          </div>

          {submitted ? (
            <div className="text-center py-12 bg-emerald-50 rounded-2xl border border-emerald-200">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-emerald-600 text-2xl" />
              </div>
              <h3 className="font-extrabold text-gray-900 text-xl mb-2">You&apos;re on the list!</h3>
              <p className="text-gray-500 text-sm max-w-xs mx-auto">
                Check your inbox — your free trial access is on the way. Start earning within minutes.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-[#fafafa] border border-gray-200 rounded-2xl p-7 flex flex-col gap-4"
            >
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name</label>
                <input
                  name="name" type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
                <input
                  name="email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Plan</label>
                <select
                  name="plan" value={form.plan}
                  onChange={(e) => setForm({ ...form, plan: e.target.value })}
                  className="w-full border border-gray-200 bg-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 cursor-pointer"
                >
                  <option value="Basic">Basic — $9.99/month</option>
                  <option value="Pro">Pro — $29.99/month</option>
                </select>
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full py-3.5 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading
                  ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" /> Processing...</span>
                  : <><i className="ri-flashlight-line mr-2" />Start Free Trial — No Card Needed</>
                }
              </button>
              <p className="text-xs text-center text-gray-400">
                14 days free · Cancel anytime · Join 12,000+ workers already earning
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
